import type { Sfx } from "../audio.ts";
import type { Surface } from "./gameplay.ts";

/**
 * Recorded sample banks (CC0, see public/audio/<pack>/License.txt) and the
 * recipe for every sound. Recipes layer recordings with short synthesized
 * body/grit/air/water; every recorded layer has a procedural stand-in used
 * while the bank is still loading or failed to load.
 */

const IMPACT = (name: string): string[] => [0, 1, 2].map((i) => `audio/impact/${name}_00${i}.ogg`);
/** ElevenLabs-generated sea-combat set (`bun run sfx`, provenance in audio/sea/sources.json). */
const SEA = (name: string): string[] => [0, 1, 2].map((i) => `audio/sea/${name}_00${i}.ogg`);

export const SAMPLE_BANKS = {
  stepWood: IMPACT("footstep_wood"),
  stepGrass: IMPACT("footstep_grass"),
  stepStone: IMPACT("footstep_concrete"),
  stepSand: IMPACT("footstep_snow"),
  woodHeavy: IMPACT("impactWood_heavy"),
  woodLight: IMPACT("impactWood_light"),
  plank: IMPACT("impactPlank_medium"),
  metalHeavy: IMPACT("impactMetal_heavy"),
  metalLight: IMPACT("impactMetal_light"),
  punch: IMPACT("impactPunch_medium"),
  creak: ["audio/rpg/creak1.ogg", "audio/rpg/creak2.ogg", "audio/rpg/creak3.ogg"],
  cloth: ["audio/rpg/cloth1.ogg", "audio/rpg/cloth2.ogg", "audio/rpg/cloth3.ogg"],
  slice: ["audio/rpg/knifeSlice.ogg", "audio/rpg/knifeSlice2.ogg"],
  coins: ["audio/rpg/handleCoins.ogg", "audio/rpg/handleCoins2.ogg"],
  latch: ["audio/rpg/metalLatch.ogg"],
  splash: ["audio/water/splash1_0.wav", "audio/water/splash2_0.wav"],
  ram: SEA("ram"),
  aground: SEA("aground"),
  hullSmash: SEA("hullSmash"),
  cannonBlast: SEA("cannonBlast"),
  cannonFar: SEA("cannonFar"),
  ballSplash: SEA("ballSplash"),
  ballGround: SEA("ballGround"),
  ballWhiz: SEA("ballWhiz"),
} as const satisfies Record<string, readonly string[]>;

export type Bank = keyof typeof SAMPLE_BANKS;

export interface FilterSpec {
  type: BiquadFilterType;
  freq: number;
  q?: number;
}

export interface SampleLayer {
  gain?: number;
  /** Playback rate before the per-event variation. */
  rate?: number;
  delay?: number;
  /** Start offset in seconds; "random" picks a point leaving `duration` of audio. */
  offset?: number | "random";
  /** Plays only this long (with a short fade) instead of the whole recording. */
  duration?: number;
  filter?: FilterSpec;
}

/** Synthesis primitives the engine provides to recipes; times are relative to the event start. */
export interface Synth {
  /** Plays a variant from the bank; false when the bank is not decoded (play a stand-in). */
  sample(bank: Bank, layer?: SampleLayer): boolean;
  tone(freq: number, duration: number, gain: number, type: OscillatorType, endFreq?: number, delay?: number): void;
  /** Filtered noise burst; "white" for air/grit, "low" (brown-ish) for rumble and water body. */
  noise(duration: number, filter: FilterSpec, gain: number, color: "white" | "low", delay?: number, attack?: number): void;
}

export interface RecipeArgs {
  surface: Surface;
  /** 0 … 1 */
  intensity: number;
}

export type Route = "world" | "ui" | "ambience";

export interface Recipe {
  /** Peak event gain before user volumes and distance. */
  gain: number;
  /** Voice-steal priority: higher survives. */
  priority: 0 | 1 | 2 | 3;
  route: Route;
  /** Beyond this listener distance (metres) the event is not started at all. */
  cull: number;
  /** Panner reference distance (metres). */
  ref: number;
  /** No pitch/level variation (UI and personal cues). */
  stable?: boolean;
  /** Briefly ducks ambience (cannon fire, heavy hull hits). */
  duck?: boolean;
  play(s: Synth, a: RecipeArgs): void;
}

const STEP_BANK: Record<Surface, Bank> = { wood: "stepWood", grass: "stepGrass", stone: "stepStone", sand: "stepSand" };
/** Snow crunch reads as sand once its bright squeak is filtered away. */
const SAND_FILTER: FilterSpec = { type: "lowpass", freq: 1500 };

function footfall(s: Synth, surface: Surface, gain: number, rate: number): void {
  const filter = surface === "sand" ? SAND_FILTER : undefined;
  if (!s.sample(STEP_BANK[surface], { gain, rate, filter, duration: surface === "grass" ? 0.35 : undefined })) {
    s.noise(0.07, { type: "lowpass", freq: surface === "wood" ? 1400 : 900 }, gain * 0.5, "low");
    if (surface === "wood") s.tone(190 * rate, 0.05, gain * 0.25, "triangle", 120);
  }
  if (surface === "sand") s.noise(0.06, { type: "highpass", freq: 3000 }, gain * 0.12, "white");
}

// Recordings peak near -1 dBFS; these keep footfalls ~10 dB over the ambience bed and heavy hits on top.
const FOOTSTEP = { gain: 0.35, priority: 0, route: "world", cull: 30, ref: 3 } as const;
const FOLEY = { gain: 0.4, priority: 1, route: "world", cull: 70, ref: 3 } as const;
const MELEE = { gain: 0.55, priority: 2, route: "world", cull: 70, ref: 3 } as const;
const HEAVY = { gain: 0.65, priority: 2, route: "world", cull: 300, ref: 10 } as const;
const PERSONAL = { gain: 0.35, priority: 3, route: "ui", cull: Infinity, ref: 3, stable: true } as const;

export const RECIPES: Record<Sfx, Recipe> = {
  pistolShot: {
    gain: 0.5, priority: 2, route: "world", cull: 180, ref: 5,
    play: (s) => {
      s.noise(0.045, { type: "highpass", freq: 1400 }, 0.9, "white");
      s.noise(0.16, { type: "lowpass", freq: 900 }, 0.65, "low");
      s.tone(145, 0.12, 0.55, "triangle", 45);
    },
  },
  pistolReload: {
    ...FOLEY, cull: 30, gain: 0.25,
    play: (s) => {
      if (!s.sample("latch", { gain: 0.65, duration: 0.18 })) s.tone(780, 0.045, 0.25, "triangle", 420);
      if (!s.sample("cloth", { gain: 0.5, duration: 0.3, delay: 0.08 })) s.noise(0.22, { type: "bandpass", freq: 1100 }, 0.2, "white", 0.08);
    },
  },
  pistolEmpty: {
    ...PERSONAL, gain: 0.18,
    play: (s) => {
      s.tone(950, 0.025, 0.5, "triangle", 450);
      s.noise(0.018, { type: "bandpass", freq: 2200 }, 0.35, "white");
    },
  },
  step: {
    ...FOOTSTEP,
    play: (s, a) => footfall(s, a.surface, 0.6 + 0.4 * a.intensity, 0.9 + 0.1 * a.intensity),
  },
  land: {
    ...FOOTSTEP,
    priority: 1,
    play: (s, a) => {
      footfall(s, a.surface, 0.7 + 0.3 * a.intensity, 0.8);
      s.tone(90, 0.14, 0.6 * a.intensity, "sine", 45);
      s.noise(0.12, { type: "lowpass", freq: 400 }, 0.5 * a.intensity, "low");
    },
  },
  swim: {
    ...FOOTSTEP,
    play: (s) => {
      if (!s.sample("splash", { offset: "random", duration: 0.3, gain: 0.8, filter: { type: "lowpass", freq: 2500 } })) {
        s.noise(0.3, { type: "bandpass", freq: 1000, q: 0.8 }, 0.5, "white", 0, 0.08);
      }
    },
  },
  splash: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      if (!s.sample("splash")) s.noise(0.45, { type: "bandpass", freq: 1400 }, 0.8, "white");
      s.noise(0.25, { type: "lowpass", freq: 300 }, 0.5, "low");
    },
  },
  bail: {
    ...FOLEY,
    play: (s) => {
      if (!s.sample("splash", { offset: "random", duration: 0.18, gain: 0.7 })) s.noise(0.18, { type: "bandpass", freq: 1200 }, 0.5, "white");
      if (!s.sample("splash", { offset: "random", duration: 0.5, gain: 0.6, delay: 0.2, filter: { type: "lowpass", freq: 1800 } })) {
        s.noise(0.5, { type: "bandpass", freq: 900 }, 0.4, "white", 0.2, 0.05);
      }
    },
  },
  scoop: {
    ...FOLEY,
    play: (s) => {
      // Wooden bucket dipped into ankle-deep deck water: a low muffled slosh.
      if (!s.sample("splash", { offset: "random", duration: 0.35, gain: 0.55, filter: { type: "lowpass", freq: 900 } })) {
        s.noise(0.35, { type: "lowpass", freq: 700 }, 0.45, "white", 0, 0.06);
      }
    },
  },
  ballSplash: {
    ...HEAVY,
    play: (s) => {
      if (!s.sample("ballSplash", { gain: 0.9 }) && !s.sample("splash", { rate: 0.85 })) s.noise(0.8, { type: "bandpass", freq: 1500 }, 0.8, "white");
      s.tone(70, 0.3, 0.45, "sine", 35);
    },
  },
  cannon: {
    ...HEAVY,
    duck: true,
    play: (s) => {
      // Layers are scaled so their coincident peaks stay near unity before the event gain.
      if (!s.sample("cannonBlast", { gain: 0.85 })) {
        s.noise(0.08, { type: "bandpass", freq: 2200 }, 0.45, "white");
        s.noise(0.9, { type: "lowpass", freq: 350 }, 0.5, "low");
        if (!s.sample("woodHeavy", { rate: 0.7, gain: 0.4, delay: 0.01 })) s.noise(0.3, { type: "lowpass", freq: 800 }, 0.3, "low", 0.01);
      }
      // Sub thump under the recording.
      s.tone(85, 0.5, 0.35, "sine", 30);
    },
  },
  /** The same shot beyond direct-blast range: a low rolling boom with echo off the sea. */
  cannonFar: {
    ...HEAVY,
    gain: 0.6,
    priority: 1,
    cull: 1500,
    ref: 60,
    play: (s) => {
      if (!s.sample("cannonFar")) {
        s.noise(1.6, { type: "lowpass", freq: 220 }, 0.7, "low", 0, 0.03);
        s.tone(60, 1.2, 0.35, "sine", 32);
      }
    },
  },
  ballHit: {
    ...HEAVY,
    duck: true,
    play: (s) => {
      if (!s.sample("hullSmash", { gain: 0.9 })) {
        if (!s.sample("woodHeavy", { rate: 0.85, gain: 0.7 })) s.noise(0.3, { type: "bandpass", freq: 1200 }, 0.6, "white");
        if (!s.sample("plank", { rate: 0.9, gain: 0.5, delay: 0.03 })) s.noise(0.4, { type: "bandpass", freq: 700 }, 0.3, "white", 0.03);
      }
      s.tone(70, 0.3, 0.35, "triangle", 35);
    },
  },
  /** Hull on rock or pier; intensity is the collision speed. */
  impact: {
    ...HEAVY,
    duck: true,
    play: (s, a) => {
      const k = 0.35 + 0.65 * a.intensity;
      if (!s.sample("aground", { gain: k, rate: 1.1 - 0.2 * a.intensity })) {
        if (!s.sample("woodHeavy", { rate: 0.7, gain: 0.7 * k })) s.noise(0.4, { type: "lowpass", freq: 600 }, 0.6 * k, "low");
        if (!s.sample("creak", { rate: 0.8, gain: 0.5 * k, delay: 0.12 })) s.tone(110, 0.5, 0.2 * k, "triangle", 80, 0.12);
      }
      s.tone(55, 0.7, 0.45 * k, "sine", 40);
    },
  },
  /**
   * Two hulls meeting at the damage speed; intensity is the closing speed. The crash, then the
   * planks of the stove-in side cracking one after another and the frames groaning.
   */
  ram: {
    ...HEAVY,
    duck: true,
    gain: 0.75,
    play: (s, a) => {
      const k = 0.35 + 0.65 * a.intensity;
      if (!s.sample("ram", { gain: k, rate: 1.1 - 0.2 * a.intensity })) {
        if (!s.sample("woodHeavy", { rate: 0.65, gain: 0.8 * k })) s.noise(0.5, { type: "lowpass", freq: 500 }, 0.7 * k, "low");
      }
      for (let i = 0; i < 4; i++) {
        const t = 0.05 + i * 0.09;
        if (!s.sample("plank", { rate: 0.7 + 0.1 * i, gain: (0.4 - 0.05 * i) * k, delay: t })) {
          s.noise(0.25, { type: "bandpass", freq: 600 + 250 * i }, (0.3 - 0.04 * i) * k, "white", t);
        }
      }
      if (!s.sample("creak", { rate: 0.6, gain: 0.55 * k, delay: 0.25 })) s.tone(95, 1.2, 0.22 * k, "triangle", 60, 0.25);
      // Both timbers taking the blow: a deep body thump under the crunch.
      s.tone(48, 0.8, 0.5 * k, "sine", 30);
      s.noise(0.6, { type: "lowpass", freq: 250 }, 0.3 * k, "low");
    },
  },
  // A stay or sheet parts: a sharp crack, the line's falling twang, and the loose end whipping away.
  ropeSnap: {
    gain: 0.45, priority: 2, route: "world", cull: 120, ref: 6,
    play: (s) => {
      s.noise(0.025, { type: "highpass", freq: 3500 }, 0.6, "white");
      s.tone(520, 0.35, 0.35, "triangle", 140);
      s.tone(780, 0.18, 0.12, "sawtooth", 260);
      if (!s.sample("slice", { rate: 1.3, gain: 0.35, delay: 0.04, filter: { type: "highpass", freq: 800 } })) {
        s.noise(0.4, { type: "bandpass", freq: 900, q: 0.8 }, 0.25, "white", 0.04, 0.12);
      }
    },
  },
  // Canvas ripping: a run of short bright tears climbing in pitch, then the torn cloth flaps.
  sailTear: {
    ...FOLEY,
    gain: 0.5,
    cull: 150,
    ref: 8,
    play: (s) => {
      for (let i = 0, t = 0; i < 5; i++, t += 0.06 + 0.03 * Math.random()) {
        s.noise(0.05 + 0.03 * Math.random(), { type: "bandpass", freq: 1500 + 400 * i + 300 * Math.random(), q: 1.4 }, 0.4, "white", t);
      }
      if (!s.sample("cloth", { rate: 1.3, gain: 0.45, duration: 0.4, filter: { type: "highpass", freq: 900 } })) {
        s.noise(0.4, { type: "bandpass", freq: 1200 }, 0.25, "white", 0, 0.04);
      }
      if (!s.sample("cloth", { rate: 0.6, gain: 0.45, delay: 0.3 })) s.noise(0.3, { type: "lowpass", freq: 400 }, 0.35, "low", 0.3, 0.05);
      s.tone(65, 0.18, 0.2, "sine", 45, 0.32);
    },
  },
  // The helm splinters: sharp cracks over the wheel breaking, then loose spokes clatter on the deck.
  wheelSmash: {
    ...HEAVY,
    gain: 0.55,
    cull: 200,
    play: (s) => {
      if (!s.sample("woodLight", { rate: 0.8, gain: 0.55 })) s.noise(0.12, { type: "bandpass", freq: 1600 }, 0.5, "white");
      if (!s.sample("plank", { rate: 1.05, gain: 0.45, delay: 0.02 })) s.noise(0.3, { type: "bandpass", freq: 800 }, 0.3, "white", 0.02);
      s.noise(0.03, { type: "highpass", freq: 2500 }, 0.35, "white");
      s.noise(0.03, { type: "highpass", freq: 2000 }, 0.3, "white", 0.07);
      s.tone(120, 0.18, 0.3, "triangle", 60);
      for (let i = 0; i < 4; i++) {
        const t = 0.18 + i * 0.11 + 0.05 * Math.random();
        s.tone(300 + 300 * Math.random(), 0.05, 0.16 - 0.02 * i, "triangle", 220, t);
      }
    },
  },
  // A gun knocked off its carriage: iron clang and timber crash, the barrel's thud, then rattling fittings.
  gunWreck: {
    ...HEAVY,
    duck: true,
    play: (s) => {
      // Layers are scaled so their coincident peaks stay near unity before the event gain.
      if (!s.sample("metalHeavy", { rate: 0.75, gain: 0.55 })) {
        s.tone(190, 1, 0.3, "sine", 180);
        s.tone(487, 0.8, 0.18, "triangle", 470);
        s.tone(803, 0.6, 0.12, "sine", 790);
      }
      if (!s.sample("woodHeavy", { rate: 0.8, gain: 0.5, delay: 0.02 })) s.noise(0.35, { type: "bandpass", freq: 900 }, 0.4, "white", 0.02);
      if (!s.sample("woodHeavy", { rate: 0.6, gain: 0.45, delay: 0.25 })) s.noise(0.3, { type: "lowpass", freq: 350 }, 0.45, "low", 0.25);
      s.tone(55, 0.5, 0.45, "sine", 35, 0.25);
      for (let i = 0; i < 3; i++) {
        const t = 0.32 + i * 0.09;
        if (!s.sample("metalLight", { delay: t, rate: 1.1 + 0.15 * i, gain: 0.3 - 0.05 * i, duration: 0.12 })) {
          s.tone(1300 + 250 * i, 0.06, 0.1, "triangle", 900, t);
        }
      }
    },
  },
  ballLand: {
    ...HEAVY,
    play: (s) => {
      if (!s.sample("ballGround", { gain: 0.9 })) {
        s.noise(0.35, { type: "lowpass", freq: 450 }, 0.8, "low");
        if (!s.sample("stepStone", { rate: 0.6, gain: 0.5 })) s.noise(0.08, { type: "bandpass", freq: 1200 }, 0.3, "white");
      }
      s.tone(65, 0.18, 0.4, "triangle", 40);
    },
  },
  /** A cannonball passing close overhead. */
  ballWhiz: {
    ...HEAVY,
    gain: 0.5,
    priority: 1,
    cull: 40,
    ref: 4,
    play: (s) => {
      if (!s.sample("ballWhiz")) s.noise(0.6, { type: "bandpass", freq: 900, q: 1.5 }, 0.6, "white", 0, 0.25);
    },
  },
  /** Your crew's shot landed on an enemy: a hollow wooden thock and a bright ping, heard at any range. */
  hitConfirm: {
    ...PERSONAL,
    gain: 0.3,
    priority: 2,
    play: (s) => {
      if (!s.sample("woodHeavy", { rate: 1.6, gain: 0.6, duration: 0.15 })) s.noise(0.06, { type: "bandpass", freq: 900 }, 0.6, "white");
      s.tone(1320, 0.18, 0.35, "sine", 1320, 0.02);
      s.tone(1980, 0.12, 0.15, "sine", 1980, 0.02);
    },
  },
  swordSwing: {
    ...MELEE,
    gain: 0.4,
    priority: 1,
    play: (s) => {
      if (!s.sample("slice", { filter: { type: "highpass", freq: 600 } })) {
        s.noise(0.18, { type: "bandpass", freq: 1800 }, 0.6, "white", 0, 0.06);
      }
    },
  },
  swordFinisher: {
    ...MELEE,
    gain: 0.5,
    priority: 1,
    play: (s) => {
      // A slower, deeper cut: heavy air before the blade, then cloth as the lunge lands.
      s.noise(0.32, { type: "bandpass", freq: 520, q: 0.7 }, 0.55, "low", 0.04, 0.12);
      if (!s.sample("slice", { rate: 0.72, filter: { type: "highpass", freq: 380 }, delay: 0.1 })) {
        s.noise(0.24, { type: "bandpass", freq: 1200 }, 0.6, "white", 0.1, 0.08);
      }
      if (!s.sample("cloth", { gain: 0.45, duration: 0.3, delay: 0.16 })) s.noise(0.2, { type: "bandpass", freq: 1100 }, 0.2, "white", 0.16, 0.04);
    },
  },
  swordHit: {
    ...MELEE,
    play: (s) => {
      if (!s.sample("punch")) {
        s.noise(0.09, { type: "bandpass", freq: 950 }, 0.7, "white");
        s.tone(130, 0.14, 0.5, "triangle", 65);
      }
      if (!s.sample("metalLight", { duration: 0.12, gain: 0.4, delay: 0.005 })) s.tone(1450, 0.08, 0.15, "sine", 700);
    },
  },
  swordHitHeavy: {
    ...MELEE,
    play: (s) => {
      // The finisher lands: a deeper body blow with a ringing edge.
      if (!s.sample("punch", { rate: 0.8 })) {
        s.noise(0.12, { type: "bandpass", freq: 700 }, 0.8, "white");
        s.tone(110, 0.2, 0.6, "triangle", 50);
      }
      if (!s.sample("woodHeavy", { rate: 1.3, gain: 0.35, delay: 0.01 })) s.noise(0.18, { type: "lowpass", freq: 500 }, 0.5, "low", 0.01);
      if (!s.sample("metalLight", { duration: 0.18, gain: 0.5, rate: 0.85, delay: 0.005 })) s.tone(1250, 0.12, 0.2, "sine", 600);
      s.tone(70, 0.28, 0.35, "sine", 42);
    },
  },
  swordBlock: {
    ...MELEE,
    play: (s) => {
      if (!s.sample("metalHeavy")) {
        s.tone(1760, 0.23, 0.35, "sine", 1550);
        s.tone(2630, 0.16, 0.2, "triangle", 2400);
      }
      s.noise(0.04, { type: "highpass", freq: 4000 }, 0.3, "white");
    },
  },
  swordParry: {
    ...MELEE,
    play: (s) => {
      // Brighter, longer ring than a plain block: the blade is deflected cleanly.
      if (!s.sample("metalHeavy", { rate: 1.25 })) s.noise(0.055, { type: "highpass", freq: 4200 }, 0.5, "white");
      s.tone(2100, 0.25, 0.35, "sine", 2800);
      s.tone(3150, 0.18, 0.2, "triangle", 3600);
    },
  },
  slide: {
    ...FOOTSTEP,
    priority: 1,
    play: (s, a) => {
      s.noise(0.45, { type: "bandpass", freq: a.surface === "wood" ? 900 : 600, q: 0.8 }, 0.9, "white", 0, 0.04);
      if (!s.sample("cloth", { gain: 0.5, duration: 0.35 })) s.noise(0.3, { type: "bandpass", freq: 1200 }, 0.2, "white", 0.05, 0.05);
    },
  },
  spyglass: {
    ...PERSONAL,
    gain: 0.18,
    priority: 1,
    play: (s) => {
      s.noise(0.025, { type: "bandpass", freq: 1800 }, 1, "white");
      s.tone(440, 0.04, 0.45, "triangle", 280);
    },
  },
  playerHit: {
    ...MELEE,
    play: (s) => {
      if (!s.sample("punch", { rate: 0.85 })) s.noise(0.15, { type: "lowpass", freq: 700 }, 0.7, "low");
      s.tone(120, 0.16, 0.4, "triangle", 60);
    },
  },
  /** One hammer blow on a nail: a bright iron tick over the plank's knock. */
  repair: {
    ...FOLEY,
    cull: 30,
    play: (s) => {
      if (!s.sample("metalLight", { rate: 1.45, gain: 0.55, duration: 0.1, filter: { type: "highpass", freq: 1400 } })) {
        s.tone(2300, 0.04, 0.25, "triangle", 1700);
      }
      if (!s.sample("plank", { duration: 0.14, gain: 0.5, rate: 1.15 })) s.noise(0.08, { type: "bandpass", freq: 900 }, 0.4, "white");
      s.tone(190, 0.06, 0.22, "triangle", 120);
    },
  },
  repaired: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      if (!s.sample("plank", { rate: 0.9 })) s.noise(0.3, { type: "bandpass", freq: 600 }, 0.6, "white");
      if (!s.sample("woodHeavy", { rate: 1.2, gain: 0.5, delay: 0.06 })) s.tone(140, 0.12, 0.4, "triangle", 90, 0.06);
    },
  },
  leak: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      if (!s.sample("woodLight", { rate: 0.8, gain: 0.7 })) s.noise(0.08, { type: "bandpass", freq: 1500 }, 0.5, "white");
      if (!s.sample("splash", { offset: "random", duration: 0.5, gain: 0.8, delay: 0.03, filter: { type: "highpass", freq: 1200 } })) {
        s.noise(0.6, { type: "bandpass", freq: 2500 }, 0.4, "white", 0.03, 0.03);
      }
    },
  },
  hullCreak: {
    gain: 0.3,
    priority: 0,
    route: "ambience",
    cull: 80,
    ref: 10,
    play: (s, a) => {
      if (!s.sample("creak", { rate: 0.8 + 0.2 * Math.random(), gain: 0.6 + 0.4 * a.intensity })) {
        s.tone(110, 0.5, 0.15, "triangle", 85);
        s.noise(0.4, { type: "bandpass", freq: 600, q: 4 }, 0.1, "white", 0, 0.1);
      }
    },
  },
  // Pawl clank as the lever is kicked, then the rope running out over the cathead while the
  // freed capstan head spins back faster and faster.
  anchorDrop: {
    ...FOLEY,
    gain: 0.5,
    play: (s) => {
      if (!s.sample("metalHeavy", { rate: 1.25, gain: 0.8 })) s.tone(220, 0.12, 0.45, "triangle", 130);
      if (!s.sample("latch", { gain: 0.6, delay: 0.03 })) s.tone(900, 0.05, 0.25, "triangle", 500, 0.03);
      s.noise(1.35, { type: "bandpass", freq: 700, q: 1.6 }, 0.34, "white", 0.12, 0.55);
      s.noise(1.25, { type: "lowpass", freq: 240 }, 0.3, "low", 0.2, 0.5);
      for (let i = 0, t = 0.16; i < 9; i++, t += 0.17 * (1 - i / 11)) {
        if (!s.sample("woodLight", { delay: t, rate: 1.1 + i * 0.05, gain: 0.2 + i * 0.03, duration: 0.08 })) {
          s.noise(0.04, { type: "bandpass", freq: 1200 }, 0.15, "white", t);
        }
      }
    },
  },
  // The anchor's own heavy plunge into the sea at the bow.
  anchorSplash: {
    ...HEAVY,
    gain: 0.55,
    play: (s) => {
      if (!s.sample("splash", { rate: 0.75 })) s.noise(0.9, { type: "bandpass", freq: 1100 }, 0.8, "white");
      s.tone(60, 0.35, 0.55, "sine", 30);
      s.noise(0.9, { type: "lowpass", freq: 500 }, 0.35, "low", 0.05, 0.05);
    },
  },
  // One pawl click over a ratchet tooth, with the capstan and rope taking the strain.
  anchorClick: {
    ...FOLEY,
    gain: 0.4,
    play: (s) => {
      if (!s.sample("metalLight", { rate: 1.35, gain: 0.55, duration: 0.1 })) s.noise(0.035, { type: "highpass", freq: 2800 }, 0.3, "white");
      if (!s.sample("creak", { offset: "random", duration: 0.35, rate: 0.75 + 0.15 * Math.random(), gain: 0.35, delay: 0.02 })) {
        s.noise(0.3, { type: "bandpass", freq: 500, q: 5 }, 0.12, "white", 0.02, 0.08);
      }
    },
  },
  // Catted: the anchor stock thumps home under the cathead (also the gate's heavy latch).
  anchor: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      for (let i = 0; i < 4; i++) {
        if (!s.sample("metalLight", { delay: i * 0.08, rate: 1 + 0.1 * i, gain: 0.5, duration: 0.12 })) {
          s.noise(0.04, { type: "highpass", freq: 2500 }, 0.3, "white", i * 0.08);
        }
      }
      if (!s.sample("metalHeavy", { delay: 0.35, rate: 0.8 })) s.tone(140, 0.2, 0.4, "triangle", 90, 0.35);
      if (!s.sample("woodHeavy", { delay: 0.37, gain: 0.6, rate: 0.8 })) s.noise(0.2, { type: "lowpass", freq: 400 }, 0.5, "low", 0.37);
    },
  },
  sail: {
    ...FOLEY,
    play: (s) => {
      if (!s.sample("cloth", { rate: 0.8 })) s.noise(0.4, { type: "bandpass", freq: 700 }, 0.5, "white", 0, 0.05);
      if (!s.sample("cloth", { rate: 0.7, gain: 0.6, delay: 0.12 })) s.noise(0.3, { type: "bandpass", freq: 500 }, 0.3, "white", 0.12, 0.05);
      s.noise(0.5, { type: "bandpass", freq: 800 }, 0.15, "white", 0, 0.1);
    },
  },
  restock: {
    ...FOLEY,
    play: (s) => {
      if (!s.sample("latch")) s.tone(900, 0.06, 0.2, "triangle", 600);
      if (!s.sample("woodLight", { delay: 0.12, gain: 0.7 })) s.noise(0.12, { type: "lowpass", freq: 900 }, 0.5, "low", 0.12);
    },
  },
  // Ship's bell: three bronze strikes, each a fundamental, octave and minor-third partial over a metal ring.
  bell: {
    ...HEAVY,
    gain: 0.5,
    play: (s) => {
      for (const d of [0, 0.42, 0.84]) {
        s.tone(587, 1.8, 0.35, "sine", 587, d);
        s.tone(1175, 1.2, 0.16, "sine", 1175, d);
        s.tone(1480, 0.8, 0.08, "triangle", 1480, d);
        s.sample("metalHeavy", { delay: d, rate: 1.6, gain: 0.35 });
      }
    },
  },
  // Lantern shutter latch and the wick catching or snuffing.
  lantern: {
    ...FOLEY,
    gain: 0.3,
    play: (s) => {
      if (!s.sample("latch")) s.tone(900, 0.06, 0.2, "triangle", 600);
      s.noise(0.25, { type: "bandpass", freq: 600 }, 0.15, "white", 0.05, 0.05);
    },
  },
  dig: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      s.noise(0.2, { type: "lowpass", freq: 500 }, 0.8, "low");
      s.noise(0.14, { type: "bandpass", freq: 1800 }, 0.3, "white", 0.01);
      if (!s.sample("metalLight", { rate: 1.3, gain: 0.3, duration: 0.08 })) s.tone(1200, 0.05, 0.08, "triangle", 900);
    },
  },
  pickup: {
    ...FOLEY,
    play: (s) => {
      if (!s.sample("cloth", { duration: 0.3 })) s.noise(0.2, { type: "bandpass", freq: 900 }, 0.4, "white", 0, 0.04);
      if (!s.sample("woodLight", { gain: 0.5, delay: 0.05 })) s.noise(0.1, { type: "lowpass", freq: 700 }, 0.4, "low", 0.05);
    },
  },
  drop: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      if (!s.sample("woodHeavy", { rate: 0.9 })) s.noise(0.15, { type: "lowpass", freq: 300 }, 0.8, "low");
      if (!s.sample("cloth", { gain: 0.4, duration: 0.25 })) s.noise(0.15, { type: "bandpass", freq: 900 }, 0.2, "white");
    },
  },
  sale: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      if (!s.sample("coins")) for (let i = 0; i < 5; i++) s.tone(2400 + (i % 3) * 400, 0.08, 0.12, "sine", undefined, i * 0.06);
      if (!s.sample("coins", { delay: 0.18, gain: 0.6 })) for (let i = 0; i < 3; i++) s.tone(2600 + i * 300, 0.08, 0.08, "sine", undefined, 0.18 + i * 0.06);
    },
  },
  treasure: {
    ...FOLEY,
    gain: 0.45,
    play: (s) => {
      [523, 659, 784].forEach((f, i) => s.tone(f, 0.22, 0.45, "sine", undefined, i * 0.08));
      if (!s.sample("woodLight", { gain: 0.5 })) s.noise(0.1, { type: "lowpass", freq: 600 }, 0.4, "low");
    },
  },
  milestone: {
    ...PERSONAL,
    play: (s) => [392, 523, 659, 784].forEach((f, i) => s.tone(f, 0.3, 0.4, "sine", undefined, i * 0.1)),
  },
  sinking: {
    ...HEAVY,
    play: (s) => {
      if (!s.sample("creak", { rate: 0.7 })) s.tone(90, 0.8, 0.3, "triangle", 60);
      if (!s.sample("creak", { rate: 0.6, gain: 0.8, delay: 0.5 })) s.tone(80, 0.8, 0.25, "triangle", 55, 0.5);
      s.noise(1.6, { type: "lowpass", freq: 300 }, 0.8, "low", 0, 0.4);
      s.tone(50, 1.2, 0.5, "sine", 35);
    },
  },
  death: {
    ...FOLEY,
    gain: 0.45,
    priority: 3,
    play: (s) => {
      if (!s.sample("punch", { rate: 0.6, gain: 0.6 })) s.noise(0.2, { type: "lowpass", freq: 400 }, 0.6, "low");
      s.tone(220, 0.6, 0.25, "triangle", 90);
      s.noise(0.5, { type: "lowpass", freq: 600 }, 0.25, "low", 0.05, 0.1);
    },
  },
  drowning: {
    ...PERSONAL,
    play: (s) => {
      for (let i = 0; i < 3; i++) s.tone(250, 0.06, 0.35, "sine", 600, i * 0.12);
      s.tone(80, 0.18, 0.6, "sine", 60);
    },
  },
  click: {
    ...PERSONAL,
    gain: 0.08,
    priority: 1,
    play: (s) => s.tone(880, 0.04, 1, "sine"),
  },
  /** Your crew is in trouble (breach, sinking, knocked out): a low iron clang and a falling two-note horn. */
  danger: {
    ...PERSONAL,
    gain: 0.22,
    priority: 2,
    play: (s) => {
      if (!s.sample("metalHeavy", { rate: 0.6, gain: 0.5 })) s.tone(140, 0.3, 0.4, "triangle", 120);
      s.tone(233, 0.22, 0.45, "sawtooth", 226, 0.02);
      s.tone(175, 0.38, 0.5, "sawtooth", 165, 0.24);
      s.tone(87, 0.6, 0.4, "sine", 80, 0.02);
    },
  },
};
